/**
 * The Suspense boundary around the agent funnel's routes (App.tsx, the one
 * allowance in DESIGN.md 5, S): the /agent/<page>/:token pages, their
 * /app/l/<location>/<page> twins and the /app account area.
 *
 * Why: every one of those pages is a lazy chunk, and without a boundary of its
 * own a page swap fell back to App.tsx's global one, a near-black sheet, in
 * the middle of a white app (and on the server-rendered first paint of every
 * token page). This fallback is the app's own page colour: white, or the dark
 * theme's #131827 when this browser saved dark. It stays mounted across the
 * funnel, so a navigation made inside startTransition (the shell's) keeps the
 * current page on screen until the next one is ready.
 *
 * It also writes the saved theme to <html data-ag-saved> while the funnel is
 * open, so the screens drawn before a page's layout mounts ("Validating
 * link", an expired link) follow the app's theme instead of the operating
 * system's (agent-app.css, standalone screens).
 *
 * Kept tiny and free of stylesheets on purpose: App.tsx loads it on every
 * page of the site, and the server renders the fallback, so it reads the
 * browser only in effects and in the client-side fallback.
 *
 * The server draws only the splash for these pages, and the browser's first
 * draw while it hydrates is that same splash; the pages come in right after.
 * The pages read the screen width, the saved theme and the clock on their
 * first draw, which the server cannot know, so hydrating them threw React
 * #418/#422 on every share link at desk width and drew the whole page twice
 * (01 Oct 26). They are private by link and never indexed, and the splash
 * covers the first paint anyway, so nothing visible is lost.
 *
 * Beside the token pages it also runs the move that takes a signed-in owner
 * off a no-login link onto their own clean address (SignedInTokenMove.tsx,
 * clean addresses PR 7). Its code loads only on an /agent page, after
 * hydration, in a boundary of its own, so no other page pays for it and it
 * never holds a page's draw.
 */
import { lazy, Suspense, useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { isAgencyMode } from '@/utils/agencyRouting';
import { BrandSplash } from './BrandSplash';
import { usePageEpoch } from './pageEpoch';

/** A chunk that cannot load (offline, a deploy replaced it) moves nothing and never breaks the page. */
const SignedInTokenMove = lazy(() =>
  import('./SignedInTokenMove').catch((err: unknown) => {
    console.warn('[FunnelBoundary] the signed-in move could not load, the page stays', err);
    return { default: () => null };
  }),
);

/**
 * The visit log and Clarity's labels for Agent Ops > Journey (AgentVisitTracker.tsx, 30 Oct 26): loaded after
 * hydration in a boundary of its own, like the signed-in move, so no page waits for it and a chunk that cannot
 * load costs nothing. Never on an agency's white-label host, which keeps every page exactly as before.
 */
const AgentVisitTracker = lazy(() =>
  import('./AgentVisitTracker').catch((err: unknown) => {
    console.warn('[FunnelBoundary] the visit log could not load, the page stays', err);
    return { default: () => null };
  }),
);

export function VisitTracking() {
  // Nothing on the server or in the hydrating draw (the server cannot know the visit), nothing on an agency host.
  const hydrated = useHydrated();
  if (!hydrated || isAgencyMode()) return null;
  return (
    <Suspense fallback={null}>
      <AgentVisitTracker />
    </Suspense>
  );
}

type Theme = 'light' | 'dark';

/** The theme this browser saved on the agent pages (?theme= wins, as the layout does). */
export function savedFunnelTheme(search?: string): Theme {
  if (typeof window === 'undefined') return 'light';
  try {
    const asked = new URLSearchParams(search ?? window.location.search).get('theme');
    if (asked === 'dark' || asked === 'light') return asked;
    return window.localStorage.getItem('fly-agent-theme') === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

/** A quiet page in the app's own colour with a small ring, while a page's code loads. */
export function FunnelLoader({ label = 'Opening', theme }: { label?: string; theme?: Theme }) {
  // The Fly Agent splash (owner, 29 Sep 26): the logo while the page's code loads; the
  // intro veil takes over from the same spot, so the logo never jumps.
  return (
    <div aria-busy="true">
      <BrandSplash theme={theme ?? savedFunnelTheme()} />
      <span role="status" className="sr-only">
        {label}
      </span>
    </div>
  );
}

/** Keeps <html data-ag-saved> on the saved theme while the funnel is open. */
function SavedThemeMark() {
  const { search, pathname } = useLocation();
  useEffect(() => {
    const apply = () => {
      document.documentElement.dataset.agSaved = savedFunnelTheme(search);
    };
    apply();
    const onToggle = () => window.setTimeout(apply, 0);
    window.addEventListener('fly-agent-theme-toggle', onToggle);
    window.addEventListener('storage', apply);
    return () => {
      window.removeEventListener('fly-agent-theme-toggle', onToggle);
      window.removeEventListener('storage', apply);
    };
  }, [search, pathname]);
  useEffect(
    () => () => {
      delete document.documentElement.dataset.agSaved;
    },
    [],
  );
  return null;
}

const noSubscribe = () => () => {};

/**
 * False on the server and in the browser's hydrating draw, true everywhere else (a page drawn
 * in the browser from scratch is true from its first draw, so it never waits a frame).
 */
function useHydrated(): boolean {
  return useSyncExternalStore(noSubscribe, () => true, () => false);
}

/** A boundary for one route element (the /app route, which is not in the funnel group). */
export function FunnelSuspense({ children }: { children: ReactNode }) {
  const hydrated = useHydrated();
  return (
    <>
      <SavedThemeMark />
      {/* The server's own splash (light: it cannot read this browser) until hydration is done. The
          boundary itself waits too: an update inside a boundary still hydrating is a React #421. */}
      {hydrated ? <Suspense fallback={<FunnelLoader />}>{children}</Suspense> : <FunnelLoader theme="light" />}
      <VisitTracking />
    </>
  );
}

/**
 * The signed-in move, on /agent pages of Fly's own hosts only: the /app pages beside them have no
 * token to leave, and an agency's white-label host keeps every page exactly as before.
 */
function TokenPageMove() {
  const { pathname } = useLocation();
  if (!pathname.startsWith('/agent/') || isAgencyMode()) return null;
  return (
    <Suspense fallback={null}>
      <SignedInTokenMove />
    </Suspense>
  );
}

/**
 * The layout route element that wraps the funnel's routes in App.tsx. Pull to
 * refresh on a location page draws the routed page again through the outlet's
 * key (pageEpoch.ts), so the page's own first reads run once more.
 */
export function FunnelBoundary() {
  const epoch = usePageEpoch();
  return (
    <FunnelSuspense>
      <Outlet key={epoch} />
      <TokenPageMove />
    </FunnelSuspense>
  );
}

export default FunnelBoundary;
